import { t, pick, getLang, setLang, onLang, languages } from '../content/i18n.js'
import vehicles from '../content/vehicles.js'
import { themes, themeOrder } from '../content/palette.js'
import site from '../content/site.js'
import { isMobileLayout } from './Utils/layout.js'

/**
 * Ekranın altındaki Menü: sekmeler Kontroller / Araç / Ortam + dil seçimi.
 * Menü üzerindeki klavye, fare, dokunma ve teker olayları oyuna geçmez (yayılım durdurulur);
 * aksi hâlde bir butona tıklamak arkadaki alanı tetikler, teker kamerayı yakınlaştırır.
 * START'tan önce araç ve tema yalnız kaydedilir; oyun başlayınca garaj ve tema bunları uygular.
 */
const ISOLATED_EVENTS = ['keydown', 'keyup', 'mousedown', 'mouseup', 'mousemove', 'click', 'touchstart', 'touchmove', 'touchend', 'wheel', 'mousewheel']

export default class Menu
{
    constructor(_options)
    {
        this.config = _options.config
        this.world = _options.world

        this.tab = 'controls'
        this.open = false
        document.documentElement.dataset.theme = this.config.theme || themeOrder[0] // START'tan önce de cam rengi temaya uysun

        this.setDom()
        this.setIsolation()
        this.setKeyboard()
        this.render()

        onLang(() => this.render())
    }

    get started()
    {
        return !!(this.world.sections && this.world.sections.garage)
    }

    setDom()
    {
        this.button = document.createElement('button')
        this.button.type = 'button'
        this.button.className = 'menu-button'
        this.button.setAttribute('aria-expanded', 'false')
        this.button.setAttribute('aria-controls', 'llp-menu')
        this.button.addEventListener('click', () =>
        {
            this.toggle()
            this.button.blur() // odak butonda kalırsa Space/Enter menüyü yeniden açar
        })

        this.panel = document.createElement('section')
        this.panel.className = 'menu'
        this.panel.id = 'llp-menu'
        this.panel.setAttribute('role', 'dialog')
        this.panel.hidden = true

        document.body.append(this.panel, this.button)
    }

    setIsolation()
    {
        for(const _element of [this.button, this.panel])
        {
            for(const _type of ISOLATED_EVENTS)
            {
                _element.addEventListener(_type, (_event) =>
                {
                    if(_type === 'keydown' && _event.key === 'Escape')
                    {
                        this.toggle(false)
                    }
                    _event.stopPropagation()
                }, { passive: _type.startsWith('touch') || _type.endsWith('wheel') })
            }
        }
    }

    setKeyboard()
    {
        window.addEventListener('keydown', (_event) =>
        {
            if(_event.key === 'Escape')
            {
                this.toggle()
            }
        })
    }

    toggle(_value = !this.open)
    {
        this.open = _value
        this.panel.hidden = !this.open
        this.button.setAttribute('aria-expanded', String(this.open))
        this.button.classList.toggle('is-active', this.open)
        if(this.open)
        {
            this.render()
            this.bindWorld()
        }
        else if(this.panel.contains(document.activeElement))
        {
            document.activeElement.blur() // odak panelde kalmasın: oyun tuşları yeniden çalışsın
        }
    }

    // Oyun başladıktan sonra garaj/tema/ses değişikliklerini menüye yansıt (bir kez bağlanır)
    bindWorld()
    {
        if(this.bound || !this.started)
        {
            return
        }
        this.bound = true
        const refresh = () => this.open && this.render()
        this.world.sections.garage.listeners.add(refresh)
        this.world.themes.listeners.add(refresh)
        this.world.sounds.muteListeners.add(refresh)
        this.world.sounds.toggleListeners.add(refresh)
    }

    selectVehicle(_id)
    {
        if(this.started)
        {
            this.world.sections.garage.select(_id)
        }
        else
        {
            this.config.vehicle = _id
            try
            {
                window.localStorage.setItem('llpause.vehicle', _id)
            }
            catch(_error) {}
        }
        this.render()
    }

    selectTheme(_name)
    {
        if(this.started)
        {
            this.world.themes.select(_name)
        }
        else
        {
            // Zemin ve sayfa arka planı hemen; gerisi Themes kurulurken config.theme'den uygulanır
            const theme = themes[_name]
            this.config.theme = _name
            Object.assign(this.world.floor.colors, theme.floor)
            this.world.floor.updateMaterial()
            document.documentElement.style.background = theme.background
            document.body.style.background = theme.background
            document.documentElement.dataset.theme = _name
            try
            {
                window.localStorage.setItem('llpause.theme', _name)
            }
            catch(_error) {}
        }
        this.render()
    }

    currentVehicle()
    {
        return this.started ? this.world.car.vehicle : (this.config.vehicle || vehicles[0].id)
    }

    render()
    {
        this.button.innerHTML = `<span>${t('menu')}</span><kbd>Esc</kbd>`
        this.button.setAttribute('aria-label', t('menu'))
        this.panel.setAttribute('aria-label', t('menu'))

        const tabs = [['controls', t('tabControls')], ['vehicle', t('tabVehicle')], ['environment', t('tabEnvironment')], ['contact', t('tabContact')]]
        this.panel.innerHTML = `
            <header class="menu__header">
                <div class="menu__tabs" role="tablist">
                    ${tabs.map(([_id, _label]) => `<button type="button" role="tab" class="menu__tab${this.tab === _id ? ' is-active' : ''}" aria-selected="${this.tab === _id}" data-tab="${_id}">${_label}</button>`).join('')}
                </div>
                <div class="menu__lang" role="group" aria-label="${t('language')}">
                    ${languages.map((_lang) => `<button type="button" class="menu__lang-option${getLang() === _lang ? ' is-active' : ''}" aria-pressed="${getLang() === _lang}" data-lang="${_lang}">${_lang.toUpperCase()}</button>`).join('')}
                </div>
                <button type="button" class="menu__close" aria-label="${t('close')}" data-close>×</button>
            </header>
            <div class="menu__body" role="tabpanel">${this.renderTab()}</div>
        `

        for(const _element of this.panel.querySelectorAll('[data-tab]'))
        {
            _element.addEventListener('click', () => { this.tab = _element.dataset.tab; this.render() })
        }
        for(const _element of this.panel.querySelectorAll('[data-lang]'))
        {
            _element.addEventListener('click', () => setLang(_element.dataset.lang))
        }
        for(const _element of this.panel.querySelectorAll('[data-vehicle]'))
        {
            _element.addEventListener('click', () => this.selectVehicle(_element.dataset.vehicle))
        }
        for(const _element of this.panel.querySelectorAll('[data-theme]'))
        {
            _element.addEventListener('click', () => this.selectTheme(_element.dataset.theme))
        }
        const close = this.panel.querySelector('[data-close]')
        close.addEventListener('click', () => this.toggle(false))
        // Ses: Tümü (M ile aynı) / Müzik / Efekt (çarpma, korna)
        for(const _element of this.panel.querySelectorAll('[data-sound]'))
        {
            _element.addEventListener('click', () =>
            {
                const sounds = this.world.sounds
                if(_element.dataset.sound === 'all')
                {
                    sounds.setMuted(!sounds.muted)
                }
                else if(_element.dataset.sound === 'music')
                {
                    sounds.setMusicEnabled(!sounds.musicEnabled)
                }
                else
                {
                    sounds.setSfxEnabled(!sounds.sfxEnabled)
                }
                this.render()
            })
        }
    }

    renderTab()
    {
        if(this.tab === 'contact')
        {
            // site.js → contact: e-posta + sosyal medya; yeni sekmede açılır (e-posta posta uygulamasında)
            const handle = (_href) =>
            {
                const url = _href.replace(/^mailto:/, '')
                if(url !== _href)
                {
                    return url
                }
                const path = new URL(_href).pathname.replace(/\/+$/, '').split('/').filter(Boolean)
                return /linkedin/.test(_href) ? path.join('/') : `@${path[path.length - 1]}`
            }
            return `
                <div class="menu__contacts menu__contacts--sites">
                    ${site.websites.map((_item) => `
                        <a class="menu__contact menu__contact--${_item.tone}" href="${_item.href}" target="_blank" rel="noopener">
                            <span class="menu__contact-label">${pick(_item.label)}</span>
                            <span class="menu__contact-handle">${new URL(_item.href).host.replace(/^www\./, '')}</span>
                            <span class="menu__contact-arrow" aria-hidden="true">↗</span>
                        </a>`).join('')}
                </div>
                <div class="menu__contacts">
                    ${site.contact.map((_item) => `
                        <a class="menu__contact" href="${_item.href}"${_item.href.startsWith('mailto:') ? '' : ' target="_blank" rel="noopener"'}>
                            <span class="menu__contact-label">${pick(_item.label)}</span>
                            <span class="menu__contact-handle">${handle(_item.href)}</span>
                            <span class="menu__contact-arrow" aria-hidden="true">↗</span>
                        </a>`).join('')}
                </div>
            `
        }

        if(this.tab === 'vehicle')
        {
            const current = this.currentVehicle()
            return `
                <div class="menu__grid">
                    ${vehicles.map((_vehicle, _index) => `
                        <button type="button" class="menu__card${_vehicle.id === current ? ' is-active' : ''}" aria-pressed="${_vehicle.id === current}" data-vehicle="${_vehicle.id}">
                            <img src="./ui/vehicles/${_vehicle.id}.png" alt="" width="480" height="300" loading="lazy">
                            <span class="menu__card-name">${pick(_vehicle.name)}</span>
                            <kbd>${_index + 1}</kbd>
                        </button>`).join('')}
                </div>
                <p class="menu__note">${t('garageNote')}${this.started ? '' : ' ' + t('startNote')}</p>
            `
        }

        if(this.tab === 'environment')
        {
            const current = this.config.theme
            const audio = this.world.sounds
            const sounds = [
                ['all', t('soundAll'), audio ? !audio.muted : true],
                ['music', t('musicToggle'), audio ? audio.musicEnabled : true],
                ['sfx', t('effectsToggle'), audio ? audio.sfxEnabled : true]
            ]
            return `
                <h2 class="menu__title">${t('themeTitle')}</h2>
                <div class="menu__grid menu__grid--themes">
                    ${themeOrder.map((_name) =>
                    {
                        const floor = themes[_name].floor
                        return `
                        <button type="button" class="menu__card${_name === current ? ' is-active' : ''}" aria-pressed="${_name === current}" data-theme="${_name}">
                            <span class="menu__swatch" style="background: linear-gradient(135deg, ${floor.topLeft}, ${floor.topRight} 45%, ${floor.bottomLeft} 70%, ${floor.bottomRight})"></span>
                            <span class="menu__card-name">${pick(themes[_name].name)}</span>
                        </button>`
                    }).join('')}
                </div>
                <div class="menu__row menu__row--sound">
                    <span>${t('sound')}</span>
                    <div class="menu__pills">
                        ${sounds.map(([_id, _label, _on]) => `<button type="button" class="menu__switch${_on ? ' is-on' : ''}" aria-pressed="${_on}" data-sound="${_id}">${_label}</button>`).join('')}
                    </div>
                </div>
            `
        }

        // Kontroller: dokunmatikte ekran kumandası (yuvarlak), klavyede Apple klavyesi gibi tuş kapakları
        const touch = this.config.touch || isMobileLayout()
        const round = (_glyph) => `<kbd class="menu__keycap menu__keycap--round">${_glyph}</kbd>`
        const rows = touch
            ? [[round('◎'), t('touchJoystick') + ' · ' + t('drive')], [round('⇈'), t('touchBoost')], [round('▲'), t('touchForward')], [round('✕'), t('touchBrake')], [round('▼'), t('touchBackward')]]
            : [
                [this.arrowKeys(), t('drive')],
                [this.keycap('shift', { symbol: '⇧', size: 'wide' }), t('boost')],
                [this.keycap('', { size: 'space', label: 'Space' }), t('brake')],
                [this.keycap('R'), t('resetCar')],
                [this.keycap('H'), t('honk')],
                [this.keycap('M'), t('mute')],
                [`${this.keycap('1')}<span class="menu__keys-to" aria-hidden="true">–</span>${this.keycap(String(vehicles.length))}`, t('pickVehicle')],
                [this.keycap('T'), t('colorTheme')],
                [this.keycap('return', { symbol: '↩', size: 'wide', align: 'end' }), t('useArea')],
                [`<span class="menu__mouse" role="img" aria-label="${t('wheelKey')}"></span>`, t('zoom')],
                [this.keycap('esc', { size: 'mod' }), t('toggleMenu')]
            ]
        return `<dl class="menu__keys${touch ? ' menu__keys--touch' : ''}">${rows.map(([_key, _label]) => `<div class="menu__key"><dt>${_key}</dt><dd>${_label}</dd></div>`).join('')}</dl>`
    }

    // Apple klavyesi tuş kapağı: harf ortada; shift / return / esc yazısı köşede (sembol üstte), space boş
    keycap(_text, _options = {})
    {
        const size = _options.size || 'letter'
        const label = _options.label ? ` aria-label="${_options.label}"` : ''
        const align = _options.align === 'end' ? ' menu__keycap--end' : ''
        if(size === 'letter')
        {
            return `<kbd class="menu__keycap">${_text}</kbd>`
        }
        const symbol = _options.symbol ? `<span class="menu__keycap-symbol">${_options.symbol}</span>` : ''
        return `<kbd class="menu__keycap menu__keycap--${size}${align}"${label}>${symbol}<span class="menu__keycap-word">${_text}</span></kbd>`
    }

    // MacBook ok tuşları: sol / sağ tam boy, yukarı / aşağı yarım boy üst üste (ters T)
    arrowKeys()
    {
        return `<span class="menu__arrows">
            <kbd class="menu__keycap menu__keycap--arrow" style="grid-area: left" aria-label="←">◀</kbd>
            <kbd class="menu__keycap menu__keycap--arrow menu__keycap--half" style="grid-area: up" aria-label="↑">▲</kbd>
            <kbd class="menu__keycap menu__keycap--arrow menu__keycap--half" style="grid-area: down" aria-label="↓">▼</kbd>
            <kbd class="menu__keycap menu__keycap--arrow" style="grid-area: right" aria-label="→">▶</kbd>
        </span>`
    }
}
